import type { CompiledChapter, JourneyMode, TimelineEvent } from "@/data/schema/compiled";
import type { Scene, SceneSpoiler } from "@/data/schema/scene";
import { appearanceByCharacter } from "../../marker/MarkerPortrait/portrait";
import { formatStoryDate, formatStoryTime } from "../../story/StoryTime/time";
import { resolveBeatPovCharacterId } from "./layoutMarkers";

export interface SceneBeat {
  sceneId: string;
  title: string;
  summary: string;
  locationLabel: string;
  locationPath?: string[];
  startMs: number;
  endMs: number;
  characterIds: string[];
  povCharacterId: string;
  appearanceByCharacter?: Record<string, string>;
  storyTimeLabel?: string;
  storyDateLabel?: string;
  chapterId?: string;
  chapterTitle?: string;
  sceneNumber?: number;
  spoilers?: SceneSpoiler[];
  keyItems?: NonNullable<Scene["keyItems"]>;
  deaths?: NonNullable<Scene["deaths"]>;
  aboard?: NonNullable<Scene["aboard"]>;
  acquires?: NonNullable<Scene["acquires"]>;
  visits?: NonNullable<Scene["visits"]>;
}

export function presentCharacterIds(scene: Scene): string[] {
  return scene.participants
    .filter((p) => p.role === "present" || p.role === "pov")
    .map((p) => p.characterId)
    .sort((a, b) => a.localeCompare(b));
}

export function chapterTitleMap(chapter: CompiledChapter): Map<string, string> {
  const titles = new Map<string, string>([[chapter.chapterId, chapter.title]]);
  for (const item of chapter.chapterTitles ?? []) {
    titles.set(item.chapterId, item.title);
  }
  return titles;
}

export function eventToBeat(
  event: TimelineEvent,
  sceneById: Map<string, Scene>,
  titles: ReadonlyMap<string, string>,
  chapter: CompiledChapter,
  journey: JourneyMode = "book",
): SceneBeat {
  const scene = sceneById.get(event.sceneId);
  const characterIds =
    scene !== undefined ? presentCharacterIds(scene) : [...event.characterIds];
  const storyTimeLabel = formatStoryTime(scene?.narrativeTime);
  const storyDateLabel = formatStoryDate(scene?.narrativeTime);
  const locationPath = event.locationPath ?? [event.locationLabel];
  const chapterId = scene?.chapterId ?? chapter.chapterId;
  const number = event.universeRank ?? scene?.bookOrder;
  return {
    sceneId: event.sceneId,
    title: event.title,
    summary: (journey === "universe" ? scene?.storySummary : undefined)
      ?? scene?.summary
      ?? event.title,
    locationLabel: locationPath[0] ?? event.locationLabel,
    locationPath,
    startMs: event.playbackMs,
    endMs: event.playbackMs + event.durationMs,
    characterIds,
    povCharacterId: resolveBeatPovCharacterId(scene, characterIds),
    appearanceByCharacter: appearanceByCharacter(scene?.participants ?? []),
    chapterId,
    chapterTitle: titles.get(chapterId) ?? chapter.title,
    ...(storyTimeLabel !== null ? { storyTimeLabel } : {}),
    ...(storyDateLabel !== null ? { storyDateLabel } : {}),
    ...(number === undefined ? {} : { sceneNumber: number }),
    ...(scene?.spoilers === undefined ? {} : { spoilers: scene.spoilers }),
    ...(scene?.keyItems === undefined ? {} : { keyItems: scene.keyItems }),
    ...(scene?.deaths === undefined ? {} : { deaths: scene.deaths }),
    ...(scene?.aboard === undefined ? {} : { aboard: scene.aboard }),
    ...(scene?.acquires === undefined ? {} : { acquires: scene.acquires }),
    ...(scene?.visits === undefined ? {} : { visits: scene.visits }),
  };
}
